import { useEffect, useId, useRef, useState } from 'react';
import type { BookingSearchHit, BookingSearchResult, Result } from '@lemontaigu/pms-core';
import { fmtDate } from './logic.js';

// « Rechercher une réservation » — the planning's search box.
//
// The desk knows a booking by what it was told: Vega's number (« 023394 »), the
// client's name or number, a group (« BOURDIER »), the OTA's reference. The
// planning shows a window of nights and cannot be scrolled to any of those.
// This box asks the database (`search`, handed in so the component names no
// infrastructure) and lists what matches; choosing one hands the hit back and
// the planning goes to it.
//
// Three rules the box keeps:
//   · an answer for what was typed a moment ago is dropped, never shown;
//   · a refusal or a failure is SAID — « nothing found » must not stand for
//     « you may not look » or « the search broke »;
//   · when there were more matches than shown, it says so.

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;

export const STATUS_FR: Partial<Record<string, string>> = {
  cancelled: 'Annulée',
  checked_in: 'En séjour',
  checked_out: 'Partie',
  no_show: 'No-show',
};

export interface RackSearchProps {
  search: (query: string) => Promise<Result<BookingSearchResult>>;
  onPick: (hit: BookingSearchHit) => void;
  /** Établissement labels — shown only when the hits span several. */
  propertyLabels?: Readonly<Record<string, string>>;
}

type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'done'; result: BookingSearchResult };

/** « ch. 310, 311 » — and « 4 chambres » beyond what reads on one line. */
export function roomsLabel(h: Pick<BookingSearchHit, 'rooms' | 'roomCount'>): string {
  if (h.roomCount === 0) return '';
  if (h.rooms.length === 0) return h.roomCount > 1 ? `${h.roomCount} logements` : 'sans chambre';
  if (h.rooms.length <= 3) return `ch. ${h.rooms.join(', ')}`;
  return `${h.rooms.length} chambres`;
}

export function RackSearch({ search, onPick, propertyLabels }: RackSearchProps) {
  const [query, setQuery] = useState('');
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const seq = useRef(0);
  const listId = useId();

  useEffect(() => {
    const q = query.trim();
    seq.current += 1;
    const mine = seq.current;
    if (q.length < MIN_CHARS) {
      setState({ kind: 'idle' });
      return;
    }
    setState({ kind: 'loading' });
    const timer = setTimeout(() => {
      void search(q).then((r) => {
        if (mine !== seq.current) return; // the desk has typed on since
        setState(r.ok ? { kind: 'done', result: r.value } : { kind: 'error', message: r.error.message });
        setActive(0);
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, search]);

  const hits = state.kind === 'done' ? state.result.hits : [];
  const showLabels = new Set(hits.map((h) => h.propertyCode)).size > 1;

  const choose = (h: BookingSearchHit) => {
    setOpen(false);
    setQuery('');
    onPick(h);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (hits.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, hits.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && open) {
      e.preventDefault();
      const h = hits[active];
      if (h !== undefined) choose(h);
    }
  };

  const panel = open && state.kind !== 'idle';

  return (
    <div className="pms-rack-search">
      <input
        type="search"
        className="pms-search"
        placeholder="Rechercher : n° de réservation, client, groupe…"
        aria-label="Rechercher une réservation"
        role="combobox"
        aria-expanded={panel}
        aria-controls={listId}
        aria-autocomplete="list"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={onKey}
      />
      {panel && (
        <div className="pms-rack-search-panel" id={listId} role="listbox" aria-label="Réservations trouvées">
          {state.kind === 'loading' && <div className="pms-rack-search-note">Recherche…</div>}
          {state.kind === 'error' && (
            <div className="pms-rack-search-note is-error" role="alert">
              {state.message}
            </div>
          )}
          {state.kind === 'done' && hits.length === 0 && (
            <div className="pms-rack-search-note">Aucune réservation ne correspond.</div>
          )}
          {hits.map((h, i) => {
            const status = STATUS_FR[h.status];
            const who = [h.groupName, h.guestName].filter((x): x is string => x !== null);
            return (
              <div
                key={h.reservationId}
                role="option"
                aria-selected={i === active}
                className={`pms-rack-search-hit ${i === active ? 'is-active' : ''} ${h.status === 'cancelled' ? 'is-cancelled' : ''}`}
                // mousedown, not click: the input's blur would close the panel first
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(h);
                }}
                onMouseEnter={() => setActive(i)}
              >
                <span className="pms-rack-search-num">{h.number ?? h.channelRef ?? '—'}</span>
                <span className="pms-rack-search-who">{who.length > 0 ? who.join(' · ') : '(sans nom)'}</span>
                <span className="pms-rack-search-when">
                  {fmtDate(h.checkinDate)} → {fmtDate(h.checkoutDate)}
                </span>
                <span className="pms-rack-search-rooms">{roomsLabel(h)}</span>
                {status !== undefined && <span className="pms-rack-search-status">{status}</span>}
                {showLabels && (
                  <span className="pms-rack-search-prop">{propertyLabels?.[h.propertyCode] ?? h.propertyCode}</span>
                )}
              </div>
            );
          })}
          {state.kind === 'done' && state.result.more && (
            <div className="pms-rack-search-note">D'autres réservations correspondent : précisez la recherche.</div>
          )}
        </div>
      )}
    </div>
  );
}
